import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { getLesson, isUnlocked, nextLessonId } from "../lib/homeKeyCurriculum";
import { LESSON_MOMENTS } from "../lib/homeKeyLessons";
import {
  getUserId,
  subscribeProgress,
  saveProgress,
  emptyProgress,
} from "../lib/homeKeyProgress";
import LessonPlayer from "../components/homeKey/LessonPlayer";
import "../styles/homekey.css";

/* ============================================================
   /home-key/lesson/:lessonId — route wrapper around the
   LessonPlayer. Guards the URL (unknown or still-locked lessons
   bounce back to the hub), and on completion merges the lesson's
   keepers into progress, stamps it complete, and returns home.
   ============================================================ */

const HomeKeyLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(emptyProgress);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const unsub = subscribeProgress(getUserId(), (p) => {
      setProgress(p);
      setLoaded(true);
    });
    return unsub;
  }, []);

  const lesson = getLesson(lessonId);
  const moments = LESSON_MOMENTS[lessonId];

  if (!loaded) {
    return (
      <div className="hk-root">
        <div className="hk-lesson">
          <p className="hk-loading">tuning up…</p>
        </div>
      </div>
    );
  }

  // Unknown lesson, unauthored lesson, or one the learner hasn't reached yet
  // (completed lessons stay revisitable) — back to the map.
  const revisitable = progress.completed[lessonId];
  if (
    !lesson ||
    !moments ||
    (!revisitable && !isUnlocked(lessonId, progress.completed))
  ) {
    return <Navigate to="/home-key" replace />;
  }

  const handleComplete = (keepers = {}) => {
    const now = Date.now();
    // LessonPlayer hands back the keepers made this run, keyed by keeperId.
    // Keepers carry different payloads by moment type — phrases, chord
    // degrees, drum patterns, layer stages, section orders, lens picks —
    // so preserve the whole record and stamp only the metadata.
    const stamped = {};
    Object.entries(keepers).forEach(([keeperId, value]) => {
      if (!value) return;
      const record = value.notes ? { phrase: value } : { ...value };
      const hasPayload = ["phrase", "pattern", "stages", "sections", "lensId", "degrees"].some(
        (key) => record[key] != null
      );
      if (!hasPayload) return;
      stamped[keeperId] = {
        ...record,
        savedAt: record.savedAt || now,
        lessonId: record.lessonId || lessonId,
      };
    });
    const nextProgress = {
      ...progress,
      completed: { ...progress.completed, [lessonId]: true },
      keepers: { ...progress.keepers, ...stamped },
      currentLessonId: nextLessonId(lessonId),
      updatedAt: now,
    };
    saveProgress(getUserId(), nextProgress);
    navigate("/home-key");
  };

  return (
    <div className="hk-root">
      <div className="hk-lesson">
        <LessonPlayer
          key={lessonId}
          lessonId={lessonId}
          lesson={lesson}
          moments={moments}
          keepers={progress.keepers}
          onComplete={handleComplete}
          onExit={() => navigate("/home-key")}
        />
      </div>
    </div>
  );
};

export default HomeKeyLesson;
